import { pageTitle } from '@palaccount/config';
import { Plus } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';

import { Ltr } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pager } from '@/components/pager';
import { usePagedState, usePageRange } from '@/lib/paging';
import { cn } from '@/lib/utils';

import { InventoryTabs } from './inventory-tabs';
import { CountStatusChip } from './stock-chips';
import { useStockCounts } from './stock-api';

/**
 * INV-05 §5.7 — the stock-take history: what was counted, where, by whom, and
 * how far the shelf was from the book. A zero-discrepancy count is good news
 * and reads green; anything else is amber and worth opening.
 */
export function CountsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  /** NFR-42 — the page rides in the URL. */
  const paging = usePagedState();
  const { data, isPending, isError } = useStockCounts(paging.page, paging.pageSize);
  const counts = data?.counts;
  const range = usePageRange(data, paging.pageSize);

  useEffect(() => {
    document.title = pageTitle(t('counts.title'));
  }, [t]);

  return (
    <div className="grid gap-4">
      <InventoryTabs />
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('counts.title')}</h2>
        <Gated permission="inventory.create" reasonKey="perm.cannot.createCounts">
          <Button asChild>
            <Link to="/inventory/counts/new">
              <Plus aria-hidden /> {t('common.new')}
            </Link>
          </Button>
        </Gated>
      </div>

      <Card>
        <CardContent>
          {isPending ? (
            <div className="h-40 animate-pulse rounded-md bg-muted" aria-busy />
          ) : isError ? (
            <p role="alert" className="py-8 text-center text-sm text-destructive">
              {t('common.loadFailed')}
            </p>
          ) : (counts ?? []).length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">{t('counts.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('counts.colCode')}</TableHead>
                  <TableHead>{t('common.date')}</TableHead>
                  <TableHead>{t('counts.colWarehouse')}</TableHead>
                  <TableHead>{t('counts.colCountedBy')}</TableHead>
                  <TableHead className="text-end">{t('counts.colDiscrepancies')}</TableHead>
                  <TableHead>{t('counts.colStatus')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(counts ?? []).map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer odd:bg-muted/30"
                    onClick={() => void navigate(`/inventory/counts/${row.id}`)}
                  >
                    <TableCell>
                      <Ltr value={row.code} className="font-mono text-sm" />
                    </TableCell>
                    <TableCell>
                      <Ltr value={row.countDate} className="font-mono text-sm" />
                    </TableCell>
                    <TableCell className="text-sm">{row.warehouseName}</TableCell>
                    <TableCell className="text-sm">{row.createdByName ?? '—'}</TableCell>
                    <TableCell
                      className={cn(
                        'text-end font-mono font-semibold',
                        row.discrepancies > 0
                          ? 'text-amber-700 dark:text-amber-400'
                          : 'text-emerald-700 dark:text-emerald-400',
                      )}
                    >
                      {row.discrepancies}
                    </TableCell>
                    <TableCell>
                      <CountStatusChip status={row.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* NFR-42 — the range, the total, and the two steps. */}
          {!isPending && !isError && (data?.total ?? 0) > 0 ? (
            <Pager
              page={paging.page}
              pages={range.pages}
              first={range.first}
              last={range.last}
              total={range.total}
              onPage={paging.setPage}
              label={t('counts.title')}
            />
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
